// ============================================================================
// AUTH & USER-SCOPED SERVICES (register, login, otp, addresses, orders)
// Hybrid: talks to Laravel when REACT_APP_USE_LIVE_API=true, otherwise uses
// browser localStorage as a mock so the SPA works offline.
// ============================================================================

import { http, callOrMock, USE_LIVE_API } from './http';

const USERS_KEY     = 'vrs_mock_users';
const ORDERS_KEY    = 'vrs_mock_orders';
const ADDRESSES_KEY = 'vrs_mock_addresses';

const readJSON  = (k, d) => { try { return JSON.parse(localStorage.getItem(k) || 'null') ?? d; } catch { return d; } };
const writeJSON = (k, v) => localStorage.setItem(k, JSON.stringify(v));

const readUsers      = () => readJSON(USERS_KEY, []);
const writeUsers     = (v) => writeJSON(USERS_KEY, v);
const readOrders     = () => readJSON(ORDERS_KEY, []);
const writeOrders    = (v) => writeJSON(ORDERS_KEY, v);
const readAddresses  = () => readJSON(ADDRESSES_KEY, []);
const writeAddresses = (v) => writeJSON(ADDRESSES_KEY, v);

const delay = (ms = 300) => new Promise(r => setTimeout(r, ms));
const fakeToken = () => 'mock_' + Math.random().toString(36).slice(2) + Date.now().toString(36);

// Helper to unwrap Laravel's default `{ data: ... }` envelope
const unwrap = (res) => res?.data !== undefined ? res.data : res;

// ---------------------------------------------------------------------------
// AUTH
// ---------------------------------------------------------------------------
export async function apiRegister({ name, email, phone, password, password_confirmation }) {
  return callOrMock(
    async () => {
      const res = await http.post('/auth/register', { name, email, phone, password, password_confirmation: password_confirmation ?? password }, { skipAuth: true });
      return unwrap(res);
    },
    async () => {
      await delay();
      const users = readUsers();
      if (users.some(u => u.email === email)) throw { message: 'This email is already registered.' };
      if (users.some(u => u.phone === phone)) throw { message: 'This mobile number is already registered.' };
      const otp = String(Math.floor(100000 + Math.random() * 900000));
      const user = {
        id: users.length + 1, name, email, phone,
        is_admin: false,
        email_verified_at: null,
        otp, otp_expires_at: Date.now() + 15 * 60 * 1000,
        created_at: new Date().toISOString(),
      };
      users.push({ ...user, password });
      writeUsers(users);
      return { message: 'Account created. A 6-digit code has been sent to your email.', email, verification_required: true, dev_otp: otp };
    },
  );
}

export async function apiVerifyOtp({ email, otp }) {
  return callOrMock(
    async () => {
      const res = await http.post('/auth/verify-otp', { email, otp }, { skipAuth: true });
      return unwrap(res);
    },
    async () => {
      await delay();
      const users = readUsers();
      const rec = users.find(u => u.email === email);
      if (!rec) throw { message: 'Account not found.' };
      if (rec.otp !== otp) throw { message: 'Invalid or expired verification code.' };
      if (rec.otp_expires_at < Date.now()) throw { message: 'This verification code has expired.' };
      rec.email_verified_at = new Date().toISOString();
      delete rec.otp; delete rec.otp_expires_at;
      writeUsers(users);
      const { password: _p, ...user } = rec;
      return { user, token: fakeToken() };
    },
  );
}

export async function apiResendOtp({ email }) {
  return callOrMock(
    async () => {
      const res = await http.post('/auth/resend-otp', { email }, { skipAuth: true });
      return unwrap(res);
    },
    async () => {
      await delay();
      const users = readUsers();
      const rec = users.find(u => u.email === email);
      if (!rec) return { message: 'If the account exists, a new code has been sent.' };
      if (rec.email_verified_at) throw { message: 'Account is already verified. Please log in.' };
      const otp = String(Math.floor(100000 + Math.random() * 900000));
      rec.otp = otp; rec.otp_expires_at = Date.now() + 15 * 60 * 1000;
      writeUsers(users);
      return { message: 'A new verification code has been sent.', dev_otp: otp };
    },
  );
}

export async function apiLogin({ login, password }) {
  return callOrMock(
    async () => {
      const res = await http.post('/auth/login', { login, password }, { skipAuth: true });
      return unwrap(res);
    },
    async () => {
      await delay();
      const users = readUsers();
      const isEmail = /@/.test(login);
      const rec = users.find(u => (isEmail ? u.email === login : u.phone === login) && u.password === password);
      if (!rec) throw { message: 'Invalid credentials' };
      if (!rec.email_verified_at) {
        const otp = String(Math.floor(100000 + Math.random() * 900000));
        rec.otp = otp; rec.otp_expires_at = Date.now() + 15 * 60 * 1000;
        writeUsers(users);
        throw { message: 'Please verify your email first.', verification_required: true, email: rec.email, dev_otp: otp };
      }
      const { password: _p, ...user } = rec;
      return { user, token: fakeToken() };
    },
  );
}

export async function apiForgot({ email }) {
  return callOrMock(
    async () => {
      const res = await http.post('/auth/forgot', { email }, { skipAuth: true });
      return unwrap(res);
    },
    async () => {
      await delay();
      const users = readUsers();
      const rec = users.find(u => u.email === email);
      if (!rec) return { message: 'If the account exists, an OTP has been sent.' };
      const otp = String(Math.floor(100000 + Math.random() * 900000));
      rec.otp = otp; rec.otp_expires_at = Date.now() + 15 * 60 * 1000;
      writeUsers(users);
      return { message: 'OTP sent.', dev_otp: otp };
    },
  );
}

export async function apiReset({ email, otp, password }) {
  return callOrMock(
    async () => {
      const res = await http.post('/auth/reset', { email, otp, password, password_confirmation: password }, { skipAuth: true });
      return unwrap(res);
    },
    async () => {
      await delay();
      const users = readUsers();
      const rec = users.find(u => u.email === email);
      if (!rec || rec.otp !== otp) throw { message: 'Invalid OTP' };
      if (rec.otp_expires_at < Date.now()) throw { message: 'OTP has expired' };
      rec.password = password;
      delete rec.otp; delete rec.otp_expires_at;
      writeUsers(users);
      return { message: 'Password updated. You can log in now.' };
    },
  );
}

// ---------------------------------------------------------------------------
// ADDRESSES (auth required)
// ---------------------------------------------------------------------------
function userScope(userId) { return `${userId ?? 'anon'}`; }

export async function apiListAddresses(userId) {
  return callOrMock(
    async () => {
      const res = await http.get('/addresses');
      return Array.isArray(res) ? res : (unwrap(res) || []);
    },
    async () => { await delay(150); return readAddresses().filter(a => a._u === userScope(userId)); },
  );
}

export async function apiCreateAddress(userId, data) {
  return callOrMock(
    async () => {
      const res = await http.post('/addresses', data);
      return unwrap(res);
    },
    async () => {
      await delay();
      const list = readAddresses();
      // If new address is default, un-default all others for this user
      if (data.is_default) list.forEach(a => { if (a._u === userScope(userId)) a.is_default = false; });
      const addr = { id: Date.now(), _u: userScope(userId), country: 'India', is_default: false, ...data };
      list.push(addr); writeAddresses(list);
      return addr;
    },
  );
}

export async function apiUpdateAddress(userId, id, data) {
  return callOrMock(
    async () => {
      const res = await http.put(`/addresses/${id}`, data);
      return unwrap(res);
    },
    async () => {
      await delay();
      const list = readAddresses();
      if (data.is_default) list.forEach(a => { if (a._u === userScope(userId)) a.is_default = false; });
      const idx = list.findIndex(a => a.id === id && a._u === userScope(userId));
      if (idx < 0) throw { message: 'Address not found' };
      list[idx] = { ...list[idx], ...data };
      writeAddresses(list);
      return list[idx];
    },
  );
}

export async function apiDeleteAddress(userId, id) {
  return callOrMock(
    async () => {
      const res = await http.del(`/addresses/${id}`);
      return unwrap(res);
    },
    async () => {
      await delay();
      writeAddresses(readAddresses().filter(a => !(a.id === id && a._u === userScope(userId))));
      return { message: 'Deleted' };
    },
  );
}

// ---------------------------------------------------------------------------
// ORDERS
// ---------------------------------------------------------------------------
export async function apiCreateOrder(payload, userId) {
  return callOrMock(
    async () => {
      const res = await http.post('/orders', payload);
      return unwrap(res);
    },
    async () => {
      await delay();
      const list = readOrders();
      const order = {
        id: 'VRS' + Date.now().toString().slice(-8),
        order_number: 'VRS' + Date.now().toString().slice(-8),
        user_id: userId || null,
        status: 'pending',
        payment_status: payload.payment_method === 'cod' ? 'pending' : 'pending',
        payment_method: payload.payment_method,
        items: payload.items,
        shipping_address: payload.shipping_address,
        subtotal: payload.subtotal,
        shipping: payload.shipping,
        discount: payload.discount || 0,
        coupon_code: payload.coupon_code || null,
        total: payload.total,
        notes: payload.notes || '',
        created_at: new Date().toISOString(),
      };
      list.unshift(order); writeOrders(list);
      return order;
    },
  );
}

export async function apiMyOrders(userId) {
  return callOrMock(
    async () => {
      const res = await http.get('/orders');
      return Array.isArray(res) ? res : (unwrap(res) || []);
    },
    async () => { await delay(150); return readOrders().filter(o => o.user_id === userId); },
  );
}

export async function apiGetOrder(id) {
  return callOrMock(
    async () => {
      const res = await http.get(`/orders/${id}`);
      return unwrap(res);
    },
    async () => { await delay(150); return readOrders().find(o => o.id === id || o.order_number === id) || null; },
  );
}

// Small helper for consumers who want to inspect the current mode
export { USE_LIVE_API };